import { styled } from '@mui/material';
import classNames from 'classnames';

import {
  AIRCRAFT_SVG_SIZE,
  AircraftIcon,
  EFlightCategory,
  type Point,
  TrialBadgeIconPath,
} from '#shared';

import { useData } from '../../data-context';
import { useGeoMap } from '../../GeoMap';
import {
  CHAR_WIDTH_FACTOR,
  getAircraftTimeSeverity,
  getAirportLabel,
  isGeoPointVisible,
  parseDonorReferencePrimary,
} from '../../helpers';
import { useOverlay } from '../model';

import type { AircraftData } from '#entities';

const PREFIX = 'TSMED-AviationAircraft';
const aviationAircraftClasses = {
  wrapper: `${PREFIX}-wrapper`,
  icon: `${PREFIX}-icon`,
  timeMark: `${PREFIX}-aircraftTimeMark`,
  timeMarkBase: `${PREFIX}-aircraftTimeMarkBase`,
  registration: `${PREFIX}-aircraftRegistration`,
  caseLabel: `${PREFIX}-aircraftCaseLabel`,
  details: `${PREFIX}-details`,
  airport: `${PREFIX}-aircraftAirport`,
  airportDeparture: `${PREFIX}-aircraftAirportDeparture`,
  airportArrival: `${PREFIX}-aircraftAirportArrival`,
};

const Root = styled('g')(({ theme: { palette, mixins, utils } }) => ({
  ...mixins.severityStyles(palette),

  ['&, & *']: {
    cursor: 'pointer',
  },

  [`.${aviationAircraftClasses.timeMarkBase}`]: {
    transition: 'all 250ms ease-in-out',
    fill: 'rgba(255,255,255,0.2)',
    transform: 'translate(5px, 28px)',
    textShadow: '0px 4px 4px rgba(0, 0, 0, 0.3)',
    height: 2,
    width: 16,
  },
  [`.${aviationAircraftClasses.timeMark}`]: {
    transition: 'all 250ms ease-in-out',
    fill: `var(--primaryColor, ${palette.grey[200]})`,
    transform: 'translate(5px, 28px)',
    textShadow: '0px 4px 4px rgba(0, 0, 0, 0.3)',
    height: 2,
    width: 'calc((var(--width) * 16px))',
  },

  [`.${aviationAircraftClasses.wrapper}`]: {
    transformOrigin: 'center',
  },

  [`.${aviationAircraftClasses.icon}`]: {
    filter: 'drop-shadow(0 32px 2px rgba(0, 0, 0, 0.3))',
    fill: 'currentColor',
    path: {
      transition: 'all 0.3s ease-in-out',
      fill: 'inherit',
      transform: 'rotate(var(--heading, 0deg))',
      transformOrigin: 'center',
    },
  },

  [`.${aviationAircraftClasses.registration}`]: {
    fill: 'currentColor',
    color: 'inherit',
    fontSize: utils.clamp(10, 12, 'sm-xl'),
    textAnchor: 'middle',
    transform: 'translate(18px, 36px)',
    textShadow: '0px 4px 4px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease-in-out',
  },
  [`.${aviationAircraftClasses.caseLabel}`]: {
    fill: 'currentColor',
    color: 'inherit',
    fontSize: utils.clamp(10, 14, 'sm-xl'),
    textAnchor: 'middle',
    transform: 'translate(18px, 36px)',
    textShadow: '0px 4px 4px rgba(0, 0, 0, 0.3)',
  },
  [`.${aviationAircraftClasses.airport}`]: {
    fill: 'currentColor',
    color: palette.grey[200],
    fontSize: utils.clamp(10, 14, 'sm-xl'),
    textAnchor: 'middle',
    transform: 'translate(18px, 26px)',
    textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
  },

  [`.${aviationAircraftClasses.details}`]: {
    visibility: 'hidden',
    display: 'none',
    opacity: 0,
    text: {
      textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
      strokeWidth: 4,
      stroke: 'black',
      paintOrder: 'stroke',
    },
  },

  [`&:hover`]: {
    [`.${aviationAircraftClasses.details}`]: {
      visibility: 'visible',
      display: 'block',
      opacity: 1,
      transition: 'opacity 250ms ease-in-out',
    },
    [`.${aviationAircraftClasses.icon}`]: {
      path: { strokeWidth: 16, stroke: 'black', paintOrder: 'stroke' },
    },
    [`.${aviationAircraftClasses.timeMarkBase}`]: {
      transform: 'translate(0px, 38px)',
      height: 4,
      width: 30,
    },
    [`.${aviationAircraftClasses.timeMark}`]: {
      transform: 'translate(0px, 38px)',
      height: 4,
      width: 'calc((var(--width) * 30px))',
    },
    [`.${aviationAircraftClasses.registration}`]: {
      fontSize: utils.clamp(10, 14, 'sm-xl'),
      transform: `translate(18px, 46px)`,
      textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
      strokeWidth: 4,
      stroke: 'black',
      paintOrder: 'stroke',
    },
  },
}));

export interface AviationAircraftProps {
  aircraftData: AircraftData;
}

export const AviationAircraft = ({ aircraftData }: AviationAircraftProps) => {
  const {
    Location,
    actualLabel,
    actualAircraftEvent,
    id,
    registration,
    airportDeparture,
    airportArrival,
    onTrialCase,
  } = aircraftData;

  const { onAircraftSelect, handleHighlightAircraft } = useOverlay();
  const { projectionRef } = useGeoMap();
  const { metarsData } = useData();

  if (!(Location.Latitude && Location.Longitude)) {
    return null;
  }

  const coordinates: Point = [Location.Longitude, Location.Latitude];

  // Hide aircraft on the far side of the globe — d3's orthographic projection
  // still returns screen coordinates for back-facing points (TSMED-1407).
  if (!isGeoPointVisible(projectionRef.current, coordinates)) {
    return null;
  }

  const { organ } = parseDonorReferencePrimary(actualLabel);
  const [sx, sy] = projectionRef.current(coordinates) as Point;

  const tx = sx - AIRCRAFT_SVG_SIZE / 2;
  const ty = sy - AIRCRAFT_SVG_SIZE / 2;

  const currentEventMs = actualAircraftEvent?.range[0]
    ? actualAircraftEvent?.range[0] - new Date().getTime()
    : 0;

  const departureLabel = getAirportLabel(
    airportDeparture?.IATA,
    airportDeparture?.ICAO,
  );
  const arrivalLabel = getAirportLabel(
    airportArrival?.IATA,
    airportArrival?.ICAO,
  );

  const departureMetarsData = airportDeparture?.ICAO
    ? metarsData[airportDeparture.ICAO]
    : undefined;
  const arrivalMetarsData = airportArrival?.ICAO
    ? metarsData[airportArrival.ICAO]
    : undefined;

  const actualSeverity = getAircraftTimeSeverity(aircraftData);

  // SVG coordinate system: aircraft icon is centered in a 36px viewBox
  // Text is positioned at x=18 (horizontal center) with textAnchor='middle'
  const AIRCRAFT_CENTER_X = 18;
  const LABEL_FONT_SIZE = 14;
  const TRIAL_ICON_GAP = 5;
  const TRIAL_ICON_SCALE = 0.8; // 80% of label font size

  const labelLength = actualLabel?.length ?? 1;
  const labelHalfWidth =
    (labelLength * LABEL_FONT_SIZE * CHAR_WIDTH_FACTOR) / 2;
  const trialXOffset = labelHalfWidth + TRIAL_ICON_GAP;
  const trialIconSize = LABEL_FONT_SIZE * TRIAL_ICON_SCALE;

  return (
    <Root
      onMouseEnter={() => handleHighlightAircraft(registration)}
      onMouseLeave={() => handleHighlightAircraft()}
    >
      <g
        className={classNames(aviationAircraftClasses.wrapper, {
          [`organ-type-${organ}`]: organ,
        })}
        transform={`translate(${Math.round(tx)}, ${Math.round(ty)})`}
        onClick={() => onAircraftSelect(id)}
      >
        <AircraftIcon
          height={AIRCRAFT_SVG_SIZE}
          width={AIRCRAFT_SVG_SIZE}
          className={aviationAircraftClasses.icon}
          style={
            {
              ['--heading']: `${(Location.Heading ?? 0) - 90}deg`,
            } as React.CSSProperties
          }
        />

        <rect className={aviationAircraftClasses.timeMarkBase} x={0} y={0} />
        <rect
          className={classNames(aviationAircraftClasses.timeMark, {
            [`severity-status-${actualSeverity}`]: actualSeverity,
          })}
          x={0}
          y={0}
          data-width={'15px'}
          style={
            {
              ['--width']:
                1 -
                Math.max(Math.min(currentEventMs / (8 * 60 * 60_000), 1), 0),
            } as React.CSSProperties
          }
        />

        <text x={0} y={10} className={aviationAircraftClasses.registration}>
          {registration}
        </text>

        <g className={aviationAircraftClasses.details}>
          <text x={0} y={36} className={aviationAircraftClasses.caseLabel}>
            {actualLabel}
          </text>
          {onTrialCase && (
            <image
              href={TrialBadgeIconPath}
              x={AIRCRAFT_CENTER_X + trialXOffset}
              y={62}
              width={trialIconSize}
              height={trialIconSize}
            />
          )}
          <text
            x={-24}
            y={60}
            className={classNames(
              aviationAircraftClasses.airport,
              aviationAircraftClasses.airportDeparture,
              `flight-category-${departureMetarsData?.flightCategory ?? EFlightCategory.NA}`,
            )}
          >
            {departureLabel}
          </text>
          <text
            x={24}
            y={60}
            className={classNames(
              aviationAircraftClasses.airport,
              aviationAircraftClasses.airportArrival,
              `flight-category-${arrivalMetarsData?.flightCategory ?? EFlightCategory.NA}`,
            )}
          >
            {arrivalLabel}
          </text>
        </g>
      </g>
    </Root>
  );
};
